import PropTypes from 'prop-types';

// libs
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
// utils
import { cn } from '@repo/zolintra-utils';
// components
import {
  TextField, Skeleton, InputsRadioGroup,
} from '@repo/zolintra-ui';
import ReadPostActions from './ReadPostActions';
// hooks
import usePost from '../../../hooks/domain/usePost';
import useSearchParams from '../../../hooks/useSearchParams';
// services
import { useRemoteWorkingPostQuery } from '../../../services/remoteWorkingPost/remoteWorkingPostQueries';
// constants
import { REMOTE_WORKING_PLACES } from '../../../constants';

import styles from './Form.module.css';

const { HOME, OTHER } = REMOTE_WORKING_PLACES;

function ReadRemoteWorkingForm({ postData }) {
  // Hooks
  const { t } = useTranslation();
  const { getDateFromSlotsId } = usePost();
  const { getSearchParams } = useSearchParams();
  const {
    year = new Date().getFullYear().toString(),
  } = getSearchParams();

  // Constants
  const { id: postId } = postData;
  const startSlotId = Number(postData.includedStartSlotId);
  const endSlotId = Number(postData.excludedEndSlotId) - 1;
  const startDate = getDateFromSlotsId({ slotId: startSlotId, year });
  const endDate = getDateFromSlotsId({ slotId: endSlotId, year });

  // API Calls
  const { data: remoteWorkingPost, isLoading } = useRemoteWorkingPostQuery({ year, postId });

  return (
    <>
      {isLoading ? (
        <>
          <div className={cn([styles['two-columns-fields'], 'margin-bottom-xs'])}>
            <Skeleton shape="rectangle" width="50%" height="16px" />
            <Skeleton shape="rectangle" width="50%" height="16px" />
          </div>
          <div className={cn([styles['two-columns-fields'], 'margin-bottom-m'])}>
            <Skeleton shape="rectangle" width="100%" height="56px" />
            <Skeleton shape="rectangle" width="100%" height="56px" />
          </div>

          <Skeleton shape="rectangle" width="30%" height="16px" className="margin-bottom-xs" />
          <Skeleton shape="rectangle" width="60%" height="48px" />
        </>
      ) : (
        <>
          <div className={cn([styles['two-columns-fields'], 'margin-bottom-m'])}>
            <TextField
              name="startDate"
              type="date"
              label={t('remoteWorkingAndLeave.readRemoteWorkingForm.date.from')}
              value={format(startDate, 'yyyy-MM-dd')}
              disabled
            />
            <TextField
              name="endDate"
              type="date"
              label={t('remoteWorkingAndLeave.readRemoteWorkingForm.date.to')}
              value={format(endDate, 'yyyy-MM-dd')}
              disabled
            />
          </div>

          <InputsRadioGroup
            name="place"
            defaultValue={remoteWorkingPost.address ? OTHER : HOME}
            label={t('remoteWorkingAndLeave.readRemoteWorkingForm.place.label')}
            options={Object.values(REMOTE_WORKING_PLACES).map((value) => ({
              value,
              label: t(`remoteWorkingAndLeave.newRemoteWorkingForm.place.${value}`),
            }))}
            className="margin-bottom-m"
            disabled

          />

          {remoteWorkingPost.address ? (
            <TextField
              name="address"
              value={remoteWorkingPost.address}
              label={t('remoteWorkingAndLeave.readRemoteWorkingForm.addressLabel')}
              className="margin-bottom-m"
              disabled
            />
          ) : null}

          {remoteWorkingPost?.comment ? (
            <>
              <p className={styles['read-only-field-label']}>{t('remoteWorkingAndLeave.readRemoteWorkingForm.commentLabel')}</p>
              <div className={styles['read-only-field']}>
                {remoteWorkingPost.comment}
              </div>
            </>
          ) : null}
        </>
      )}

      <ReadPostActions
        postData={postData}
        year={year}
        isPostsListLoading={isLoading}
      />
    </>
  );
}

ReadRemoteWorkingForm.propTypes = {
  postData: PropTypes.shape({
    id: PropTypes.string,
    includedStartSlotId: PropTypes.number,
    excludedEndSlotId: PropTypes.number,
    status: PropTypes.oneOf(['Pending', 'Accepted', 'Refused']),
  }).isRequired,
};

ReadRemoteWorkingForm.defaultProps = {};

export default ReadRemoteWorkingForm;
